/**
 * InternationalTransporterDashboardBody — main-dashboard body for the
 * `international_transporter` persona.
 *
 * State machine (mirrors `GET /api/me/transport/international/status`):
 *   - "none"             → Apply CTA panel + pricing
 *   - "pending_payment"  → Finish $9 verification fee (link to Stripe URL)
 *   - "pending_review"   → "Under review" panel
 *   - "rejected"         → Polite explanation
 *   - "approved"         → Live panel: credits, Pro sub, recent leads,
 *                          directory link
 */
import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import TripUpdatesPanel from "@/components/TripUpdatesPanel";
import { AirplaneTiltIcon as AirplaneTilt, ShieldCheckIcon as ShieldCheck, CurrencyDollarIcon as CurrencyDollar, ClockIcon as Clock, CheckCircleIcon as CheckCircle, WarningIcon as Warning, StarIcon as Star, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

export default function InternationalTransporterDashboardBody() {
  const [status, setStatus] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/me/transport/international/status")
      .then((r) => { if (!cancelled) setStatus(r.data || { state: "none" }); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, []);

  if (loadFailed) return <ApplyPanel />;
  if (!status) return <Skeleton />;

  const state = status.state || "none";
  if (state === "approved") return <ApprovedPanel status={status} />;
  if (state === "pending_payment") return <PendingPaymentPanel status={status} />;
  if (state === "pending_review") return <PendingReviewPanel />;
  if (state === "rejected") return <RejectedPanel />;
  return <ApplyPanel />;
}

function Skeleton() {
  return (
    <div className="space-y-3" data-testid="intl-dash-skeleton">
      <div className="h-32 bg-[#FBF7EC] rounded-xl animate-pulse" />
    </div>
  );
}

function ApplyPanel() {
  return (
    <div
      className="bg-white border border-[#E5D08A] rounded-xl p-6 sm:p-8"
      data-testid="intl-dash-apply"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-full bg-[#FBF7EC] border border-[#E5D08A] flex items-center justify-center">
          <AirplaneTilt size={28} weight="duotone" className="text-[#8A6A00]" />
        </div>
        <div className="flex-1">
          <h2 className="font-spectral text-2xl tracking-tight">
            Become a verified international courier
          </h2>
          <p className="text-[13px] text-[#5C5C5C] mt-1 leading-relaxed">
            Get on the public directory, receive verified buyer leads
            in your inbox, and grow your client list. Armstrong never
            touches your money — you set your own quotes.
          </p>
        </div>
      </div>
      <ul className="text-[13px] text-[#3a3a3a] space-y-2 mt-4">
        <Bullet>$9 one-time verification — <strong>your first lead is free</strong></Bullet>
        <Bullet>$29 per lead after that — pay only when a real request matches you</Bullet>
        <Bullet>Optional Pro · $199/mo — unlimited leads + featured placement</Bullet>
        <Bullet>Leads sent directly to your email — reply directly to the buyer</Bullet>
        <Bullet>No platform cut on transport quotes</Bullet>
      </ul>
      <Link to="/transport/international/apply">
        <Button
          className="rounded-full bg-crimson hover:bg-ink text-white mt-5"
          data-testid="intl-dash-cta-apply"
        >
          Start application <ArrowRight size={14} className="ml-1" />
        </Button>
      </Link>
    </div>
  );
}

function PendingPaymentPanel({ status }) {
  return (
    <div
      className="bg-white border border-[#E5D08A] rounded-xl p-6"
      data-testid="intl-dash-pending-payment"
    >
      <div className="flex items-center gap-2 mb-2">
        <Clock size={20} weight="duotone" className="text-[#8A6A00]" />
        <h2 className="font-spectral text-xl">Finish your application</h2>
      </div>
      <p className="text-[13px] text-[#5C5C5C] leading-relaxed">
        Your application is saved but the $9 verification fee hasn&rsquo;t been
        processed yet. Complete payment to send it for review — it includes
        your first lead free.
      </p>
      {status.stripe_checkout_url ? (
        <a href={status.stripe_checkout_url}>
          <Button className="rounded-full bg-crimson hover:bg-ink text-white mt-4">
            Pay $9 verification fee
          </Button>
        </a>
      ) : (
        <Link to="/transport/international/apply">
          <Button variant="outline" className="rounded-full mt-4">
            Restart application
          </Button>
        </Link>
      )}
    </div>
  );
}

function PendingReviewPanel() {
  return (
    <div
      className="bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-6"
      data-testid="intl-dash-pending-review"
    >
      <div className="flex items-center gap-2 mb-2">
        <Clock size={20} weight="duotone" className="text-[#8A6A00]" />
        <h2 className="font-spectral text-xl">Under review</h2>
      </div>
      <p className="text-[13px] text-[#5C5C5C] leading-relaxed">
        Thanks &mdash; your $9 verification fee was received. We&rsquo;re verifying your
        credentials (CDC permit, IPATA, IATA, insurance). Expect a
        decision within 5 business days at the email on file. Once
        approved, your first lead credit is waiting for you.
      </p>
    </div>
  );
}

function RejectedPanel() {
  return (
    <div
      className="bg-white border border-[#E5D08A] rounded-xl p-6"
      data-testid="intl-dash-rejected"
    >
      <div className="flex items-center gap-2 mb-2">
        <Warning size={20} weight="duotone" className="text-[#C1272D]" />
        <h2 className="font-spectral text-xl">Application not approved</h2>
      </div>
      <p className="text-[13px] text-[#5C5C5C] leading-relaxed">
        Unfortunately we couldn&rsquo;t approve your application at this time.
        If you believe this was in error or have updated credentials,
        reach out to international@armstrongk9registry.com.
      </p>
    </div>
  );
}

function ApprovedPanel({ status }) {
  const t = status.transporter || {};
  const lp = status.lead_pass || {};
  const [leads, setLeads] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/me/transport/international/leads")
      .then((r) => setLeads(r.data?.leads || []))
      .catch(() => {});
  }, []);

  const subscribe = async (plan) => {
    setBusy(true);
    try {
      const r = await api.post("/me/transport/international/subscribe", {
        plan,
        origin_url: webOrigin(),
      });
      if (r.data?.url) window.location.assign(r.data.url);
      else if (r.data?.already_subscribed) toast.info("Already subscribed.");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not start subscription."
      );
    } finally {
      setBusy(false);
    }
  };

  const buyCredits = async () => {
    setBusy(true);
    try {
      const r = await api.post("/me/transport/international/buy-credits", {
        origin_url: webOrigin(),
      });
      if (r.data?.url) window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="intl-dash-approved">
      {/* Header tile */}
      <div className="bg-white border border-[#E5D08A] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={22} weight="duotone" className="text-[#1A6E36]" />
          <h2 className="font-spectral text-2xl">
            Verified courier · {t.display_name}
          </h2>
        </div>
        <Link
          to={`/transport/international/${status.slug}`}
          className="text-[12px] text-crimson hover:text-ink underline-offset-2 hover:underline"
        >
          View your public profile →
        </Link>
      </div>

      {/* Leads & pricing */}
      <div className="bg-white border border-[#E5D08A] rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <CurrencyDollar size={20} weight="duotone" className="text-[#8A6A00]" />
          <h3 className="font-spectral text-xl">Lead credits</h3>
        </div>
        {lp.active ? (
          <div className="text-[13px] text-[#1A6E36] flex items-center gap-2 mb-3">
            <CheckCircle size={16} weight="fill" /> Pro Subscription Active ({lp.plan}) &mdash; unlimited leads + featured placement
          </div>
        ) : (
          <p className="text-[13px] text-[#5C5C5C] leading-relaxed mb-3">
            Your first lead credit came free with verification. After that,
            pay $29 per lead &mdash; or go unlimited with Pro.
          </p>
        )}
        <div className="grid grid-cols-1 gap-2 mb-4 text-center text-[11px] max-w-[180px]">
          <div className="bg-[#FBF7EC] border border-[#F0EAD6] rounded-lg p-2">
            <div className="text-[#8A6A00] font-bold uppercase tracking-wider">Lead credits</div>
            <div className="font-spectral text-2xl" data-testid="intl-dash-credits">{status.credits ?? 0}</div>
          </div>
        </div>
        {!lp.active && (
          <>
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                disabled={busy}
                onClick={buyCredits}
                variant="outline"
                className="rounded-full text-[12px]"
                data-testid="intl-dash-buy-1"
              >
                +1 lead · $29
              </Button>
              <Button
                disabled={busy}
                onClick={() => subscribe("monthly")}
                className="rounded-full bg-crimson hover:bg-ink text-white"
                data-testid="intl-dash-sub-monthly"
              >
                Pro · $199 / mo (unlimited)
              </Button>
              <Button
                disabled={busy}
                onClick={() => subscribe("yearly")}
                variant="outline"
                className="rounded-full"
                data-testid="intl-dash-sub-yearly"
              >
                $1,990 / yr (save ~16%)
              </Button>
            </div>
            <p className="text-[11px] text-[#8B8B8B] flex items-center gap-1">
              <Star size={12} weight="duotone" /> Pro includes featured placement at the top of the public directory.
            </p>
          </>
        )}
      </div>

      {/* Recent leads */}
      <div className="bg-white border border-[#E5D08A] rounded-xl p-6">
        <h3 className="font-spectral text-xl mb-3">Recent leads</h3>
        {leads.length === 0 ? (
          <p className="text-[13px] text-[#8B8B8B]">
            No leads yet. They&rsquo;ll show up here AND be emailed to you the
            moment a buyer requests a quote.
          </p>
        ) : (
          <ul className="space-y-2">
            {leads.slice(0, 10).map((l) => (
              <li
                key={l.id}
                className="text-[13px] py-2 border-b border-[#F0EAD6] last:border-none"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <div>
                    <strong>{l.origin_country} → {l.destination_country}</strong>
                    {l.destination_city && <> ({l.destination_city})</>} ·{" "}
                    <span className="text-[#6B6B6B]">{l.buyer_name}</span>
                  </div>
                  <div className="text-[11px] text-[#8B8B8B]">
                    {l.lead_delivered_via_email ? "✉ delivered" : "queued"} ·{" "}
                    {new Date(l.created_at).toLocaleDateString()}
                  </div>
                </div>
                <LeadUpdatesToggle leadId={l.id} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle
        size={16}
        weight="fill"
        className="text-[#1A6E36] mt-0.5 flex-shrink-0"
      />
      <span>{children}</span>
    </li>
  );
}

function LeadUpdatesToggle({ leadId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-crimson hover:text-ink mt-1 underline-offset-2 hover:underline"
        data-testid={`lead-toggle-updates-${leadId}`}
      >
        {open ? "Hide trip updates" : "Post / view trip updates →"}
      </button>
      {open && (
        <div className="mt-3 bg-[#FBF7EC] border border-[#E5D08A] rounded-lg p-3">
          <TripUpdatesPanel tripKind="intl_lead" tripId={leadId} />
        </div>
      )}
    </>
  );
}
